import type { RouteLocationNormalized } from 'vue-router';
import { useBroadcastChannel, useSessionStorage } from '@vueuse/core';

/**
 * Campaign parameters stored as key-value pairs
 */
type AdCampaignCookie = Record<string, string>;

/**
 * Return type for the useAdCampaign composable
 */
interface UseAdCampaignReturn {
	/** Campaign data stored in sessionStorage and synced across tabs */
	adCampaign: Ref<AdCampaignCookie>;
	/** Whether there are active campaign parameters in the current session */
	hasCampaignInSession: ComputedRef<boolean>;
	/** Check if a route contains any recognized campaign parameters */
	routeHasCampaignParams: (route: RouteLocationNormalized) => boolean;
	/** Extract and normalize campaign parameters from a route's query */
	getCampaignParamsFromRoute: (
		route: RouteLocationNormalized
	) => AdCampaignCookie;
	/** Update campaign parameters and broadcast changes to other tabs */
	setAdCampaign: (campaignParams: AdCampaignCookie) => void;
	/** Get current campaign parameters */
	getAdCampaign: () => AdCampaignCookie;
}

/**
 * Message structure for cross-tab communication
 */
interface BroadcastMessage {
	/** Message type for synchronization between tabs */
	type: 'SYNC_REQUEST' | 'UPDATE';
	/** Campaign data being shared */
	data: AdCampaignCookie;
}

/**
 * Composable for managing advertising campaign parameters with cross-tab synchronization.
 *
 * Features:
 * - Session persistence using sessionStorage
 * - Cross-tab synchronization using BroadcastChannel
 * - Automatic data sync when new tabs open
 * - UTM parameter normalization
 *
 * @example
 * ```ts
 * const { adCampaign, setAdCampaign, getCampaignParamsFromRoute } = useAdCampaign();
 *
 * // Handle route with campaign parameters
 * const route = useRoute();
 * if (routeHasCampaignParams(route)) {
 *   const params = getCampaignParamsFromRoute(route);
 *   setAdCampaign(params);
 * }
 *
 * // Access campaign data
 * const campaignId = computed(() => adCampaign.value.adCampaignId);
 * ```
 *
 * @returns Methods and reactive data for campaign management
 */
export function useAdCampaign(): UseAdCampaignReturn {
	/** Supported campaign parameter keys in URLs */
	const campaignParams: string[] = [
		'ad_campaign_id',
		'ad_set_id',
		'ad_id',
		'ad_name',
		'ad_set',
		'ad_campaign',
		'utm_id',
		'utm_term',
		'utm_content',
		'utm_campaign',
		'utm_source',
		'utm_medium'
	];

	/** Mapping of UTM parameters to their normalized campaign parameter names */
	const paramMapping = {
		utm_id: 'ad_campaign_id',
		utm_source: 'ad_set_id',
		utm_medium: 'ad_id',
		utm_term: 'ad_name',
		utm_content: 'ad_set',
		utm_campaign: 'ad_campaign'
	} as const;

	// Persistent storage with cross-tab sync
	const adCampaign = useSessionStorage<AdCampaignCookie>('ad_campaign', {});

	const {
		isSupported,
		post,
		data: campaignChannelData
	} = useBroadcastChannel<BroadcastMessage, BroadcastMessage>({
		name: 'ad_campaign_channel'
	});

	// Handle cross-tab message synchronization
	watch(
		campaignChannelData,
		newData => {
			if (!newData?.data) return;

			const messageHandlers = {
				SYNC_REQUEST: () => {
					if (Object.keys(adCampaign.value).length > 0) {
						post({
							type: 'UPDATE',
							data: { ...adCampaign.value }
						});
					}
				},
				UPDATE: () => {
					adCampaign.value = { ...newData.data };
				}
			};

			messageHandlers[newData.type]?.();
		},
		{ immediate: true }
	);

	// Request data sync when a new tab opens
	watch(
		isSupported,
		isReallySupported => {
			if (isReallySupported) {
				post({
					type: 'SYNC_REQUEST',
					data: {}
				});
			}
		},
		{ immediate: true }
	);

	const hasCampaignInSession = computed<boolean>(
		() => Object.keys(adCampaign.value).length > 0
	);

	/**
	 * Check if a route contains any recognized campaign parameters
	 * @param route - Vue Router route object to check
	 * @returns Whether the route contains campaign parameters
	 */
	function routeHasCampaignParams(route: RouteLocationNormalized): boolean {
		const { query } = route;
		return campaignParams.some(key => key in query);
	}

	/**
	 * Extract and normalize campaign parameters from a route
	 * @param route - Vue Router route object containing query parameters
	 * @returns Normalized campaign parameters
	 */
	function getCampaignParamsFromRoute(
		route: RouteLocationNormalized
	): AdCampaignCookie {
		const { query } = route;
		const campaignData: AdCampaignCookie = {};

		for (const key of Object.keys(query)) {
			if (!campaignParams.includes(key) || typeof query[key] !== 'string')
				continue;

			if (!key.startsWith('utm_')) {
				campaignData[useCamelCase(key)] = query[key] as string;
				continue;
			}

			const mappedKey = paramMapping[key as keyof typeof paramMapping];
			campaignData[useCamelCase(mappedKey)] = query[key] as string;
		}

		return campaignData;
	}

	/**
	 * Update campaign parameters and broadcast changes to other tabs
	 * @param campaignParams - New campaign parameters to store
	 */
	function setAdCampaign(campaignParams: AdCampaignCookie): void {
		adCampaign.value = campaignParams;

		if (isSupported.value) {
			post({
				type: 'UPDATE',
				data: campaignParams
			});
		}
	}

	/**
	 * Get current campaign parameters
	 * @returns Current campaign parameters from sessionStorage
	 */
	function getAdCampaign(): AdCampaignCookie {
		return unref(adCampaign.value);
	}

	return {
		adCampaign,
		hasCampaignInSession,
		routeHasCampaignParams,
		getCampaignParamsFromRoute,
		setAdCampaign,
		getAdCampaign
	};
}
